import React, { useMemo, useRef, useState, useEffect } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSFormSelect from "../../../ui-components/form/select/xgsFormSelect";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import { useQuery, useMutation } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import LoadBoardService from "../../../app/data/load-board/loadBoardService";
import CarrierService from "../../../app/data/carriers/carriersServices";
import EquipmentService from "../../../app/data/equipment/equipmentServices";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import Table from "../../../ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import AddSecondaryDistributorModal from "./addSecondaryDistributorModal";
import { DoorType, DoorUsage } from "../../../app/data/load-board/enums";
import type { AssignDoorRequest } from "../../../app/data/load-board/loadBoardService";
import {
  AssignDoorModalProps,
  AssignDoorFormValues,
  SecondaryDistributor,
  PRIORITY_OPTIONS,
  DOOR_TYPE_OPTIONS,
  DOOR_USAGE_OPTIONS,
} from "./models";
import "./assignDoorModal.scss";
import { toast } from "react-toastify";
import { ReactComponent as DeleteIcon } from "../../../images/trash.svg";
import ManifestsService from "app/data/manifest/manifestsService";
import { DriverSearch } from "app/data/manifest/models";

const loadBoardService = LoadBoardService.getInstance();
const carrierService = CarrierService.getInstance();
const equipmentService = EquipmentService.getInstance();
const manifestService = ManifestsService.getInstance();

const columnHelper = createColumnHelper<SecondaryDistributor>();

const XGS_CARRIER_ID = "1325";

const getInitialValues = (initialValues?: Partial<AssignDoorFormValues>): AssignDoorFormValues => {

  const permanentOption = DOOR_USAGE_OPTIONS.find(opt => opt.value === DoorUsage.PERMANENT);
  const carrierValue = initialValues?.carrierNumber !== undefined && initialValues?.carrierNumber !== null
    ? initialValues.carrierNumber
    : XGS_CARRIER_ID;
  return {
    doorNumber: initialValues?.doorNumber || "",
    priority: initialValues?.priority || "1",
    driverId: initialValues?.driverId || "",
    driverName: initialValues?.driverName || "",
    carrierNumber: carrierValue,
    secondaryDistributorDetails: initialValues?.secondaryDistributorDetails || [],
    trailer: initialValues?.trailer || "",
    doorUsage: initialValues?.doorUsage || (permanentOption?.value as string) || "",
    doorType: initialValues?.doorType || "",
    tripNumber: initialValues?.tripNumber || "",
    cutDate: initialValues?.cutDate || "",
  };
};

const validationSchema = Yup.object().shape({
  doorNumber: Yup.string().required("Door is required"),
  priority: Yup.string().required("Priority is required"),
  carrierNumber: Yup.number().required("Carrier is required"),
  trailer: Yup.string().required("Trailer is required"),
  doorUsage: Yup.string().required("Door Usage is required"),
  doorType: Yup.string().required("Door Type is required"),
});

const parseOriginTerminalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : undefined;
};

const AssignDoorModal: React.FC<AssignDoorModalProps> = (props) => {
  const originTerminalNumber = parseOriginTerminalNumber(props.initialValues?.originTerminalNumber);
  const formikRef = useRef<FormikProps<AssignDoorFormValues>>(null);

  const initialFormValues = useMemo(() => getInitialValues(props.initialValues), [props.initialValues]);

  const [carrierFormValue, setCarrierFormValue] = useState<XGSSelectOption | null>(null);
  const [, setCarrierSearch] = useState("");

  const [trailerFormValue, setTrailerFormValue] = useState<XGSSelectOption | null>(null);
  const [driverFormValue, setDriverFormValue] = useState<XGSSelectOption | null>(null);
  const [, setTrailerSearch] = useState("");

  const [secondaryDistributors, setSecondaryDistributors] = useState<SecondaryDistributor[]>([]);
  const [showAddDistributorModal, setShowAddDistributorModal] = useState(false);

  const mapSecondaryDistributors = (details: any[] = []): SecondaryDistributor[] => {
    return details.map((d) => ({
      id: d.accountId.toString(),
      accountNumber: d.accountNumber?.toString() || "",
      name: d.accountName || "",
      attention: d.accountAttentionName || "",
      address: `${d.address}, ${d.city}, ${d.state} ${d.zip}`,
    }));
  };

  //eslint-disable-next-line
  const loadDriverOptions = React.useCallback(
    debounce((query, callback) => {
      manifestService.getDriversSearch(query)
        .then(options => callback(options.map(({ driver, searchDetails }: DriverSearch) => ({
          // label: <span dangerouslySetInnerHTML={{ __html: searchDetails.matchedStr }} />
          // label: searchDetails.matchedStr.replace(/<[^>]+>/g, "")          
          value: driver.driverId,
          label: searchDetails.matchedStr.replace(/<[^>]+>/g, "")
        }))))
    }, 300),
    []
  );

  // Reset form and state when modal opens with new initial values
  useEffect(() => {
    if (props.show && props.initialValues) {

      const newInitialValues = getInitialValues(props.initialValues);
      // Reset form with new values
      if (formikRef.current) {
        formikRef.current.resetForm({ values: newInitialValues });
      }
      // Set search states to initial values so async options include them
      setCarrierSearch(newInitialValues.carrierNumber?.toString() || "");
      setTrailerSearch(newInitialValues.trailer || "");
      // Set carrier form value if initial value exists
      if (newInitialValues.carrierNumber) {
        carrierService
          .getCarrierListQuery(newInitialValues?.carrierNumber, 0)
          .then(res => {
            const c = res.data.content?.[0];
            if (!c) return;

            setCarrierFormValue({
              label: `${c.carrierName} (${c.carrierNumber})`,
              value: c.carrierNumber.toString()
            });
          });
      } else {
        setCarrierFormValue(null);
      }

      if (newInitialValues.driverId) {
        manifestService
          .getDriversSearch(newInitialValues.driverId)
          .then(list => {
            const d = list?.[0];
            if (!d) return;

            setDriverFormValue({
              label: d.driver.driverName,
              value: d.driver.driverId
            });
          });
      }
      else {
        setDriverFormValue(null);
      }
      // Set trailer form value if initial value exists
      if (newInitialValues.trailer) {
        setTrailerFormValue({
          label: newInitialValues.trailer,
          value: newInitialValues.trailer
        });
      } else {
        setTrailerFormValue(null);
      }
      if (newInitialValues?.secondaryDistributorDetails?.length) {
        setSecondaryDistributors(
          mapSecondaryDistributors(newInitialValues.secondaryDistributorDetails)
        );
      } else {
        setSecondaryDistributors([]);
      }
    } else if (!props.show) {
      // Reset when modal closes
      setCarrierSearch("");
      setTrailerSearch("");
      setCarrierFormValue(null);
      setTrailerFormValue(null);
      setSecondaryDistributors([]);
    }
  }, [props.show, props.initialValues]);

  const { data: doors, isLoading: isDoorsLoading } = useQuery({
    queryKey: [QUERY_KEYS.LOAD_BOARD_DOORS, originTerminalNumber],
    queryFn: async () => {
      if (!originTerminalNumber) return [];
      const response = await loadBoardService.getDoors(originTerminalNumber);
      return response.data || [];
    },
  });

  const getPriorityInteger = (priority: string): number => {
    const priorityNum = parseInt(priority, 10);
    return isNaN(priorityNum) ? 1 : priorityNum;
  };

  const assignDoorMutation = useMutation({
    mutationFn: async (request: AssignDoorRequest) => {
      return await loadBoardService.assignDoor(request);
    },
    onSuccess: (res: any) => {
      if (props.onSave) {
        props.onSave({
          doorNumber: formikRef.current?.values.doorNumber,
          carrierNumber: formikRef.current?.values.carrierNumber,
          driverId: formikRef.current?.values.driverId || "",
          driverName: formikRef.current?.values.driverName || "",
          trailer: formikRef.current?.values.trailer,
          priority: formikRef.current?.values.priority,
          primaryDistributor: props.destinationTerminal.toString(),
          secondaryDistributors: secondaryDistributors,
          doorUsage: formikRef.current?.values.doorUsage,
          doorType: formikRef.current?.values.doorType,
          tripNumber: props.initialValues?.tripNumber || "",
          cutDate: props.initialValues?.cutDate?.toApiDateFormat() || "",
        });
      }
      toast.info("Door assigned successfully.");
      resetAllFields();
      handleClose();
    },
    onError: (error: any) => {

      const message =
        error?.message ||
        error[0]?.message ||
        "Something went wrong";

      toast.error(message);

      setTimeout(() => handleClose(), 300);
    },
  });

  const onSubmit = (values: AssignDoorFormValues) => {
    if (
      !values.doorNumber ||
      !values.carrierNumber ||
      !values.trailer ||
      !values.priority ||
      !values.doorUsage ||
      !values.doorType ||
      originTerminalNumber == null
    ) {
      return;
    }

    // Transform secondaryDistributors to array of integers
    const secondaryDistributorNumbers = secondaryDistributors
      .map(d => parseInt(d.accountNumber || "", 10))
      .filter(num => !isNaN(num));

    // Get door status from selected door or default
    // const selectedDoor = doors?.find(d => d.doorNumber === values.door);
    const doorStatus = null;

    // Prepare the API request - use origin terminal of the active trip, not current terminal
    const request: AssignDoorRequest = {
      terminalNumber: originTerminalNumber,
      doorNumber: values.doorNumber,
      primaryDistributor: props.destinationTerminal,
      secondaryDistributors: secondaryDistributorNumbers,
      driverId: values.driverId ? parseInt(values.driverId, 10) : undefined,
      driverName: values.driverName,
      trailerNumber: parseInt(values.trailer, 10),
      manifest: props.manifestNumber,
      carrierNumber: parseInt(values.carrierNumber, 10),
      priority: getPriorityInteger(values.priority),
      doorStatus: doorStatus,
      doorUsage: values.doorUsage,
      doorType: values.doorType as DoorType,
      tripNumber: props.initialValues?.tripNumber != null ? String(props.initialValues.tripNumber) : "",
      cutDate: props.initialValues?.cutDate?.toApiDateFormat() || "",
    };

    assignDoorMutation.mutate(request);
    setTimeout(() => {
      if (assignDoorMutation.isLoading) {
        assignDoorMutation.cancel?.();
        toast.error("Request took too long. Cancelled.");
      }
    }, 5000);
  };

  const handleClose = () => {
    setTimeout(() => {
      if (assignDoorMutation.isLoading) {
        assignDoorMutation.cancel?.();
      }
    }, 5000);
    resetAllFields();
    props.onClose();
  };


  const handleRemoveDistributor = (id: string) => {
    setSecondaryDistributors(prev => prev.filter(d => d.id !== id));
  };

  const handleAddSecondaryDistributor = () => {
    setShowAddDistributorModal(true);
  };

  const handleAddDistributor = (distributor: SecondaryDistributor) => {
    setSecondaryDistributors(prev => {
      if (prev.some(d => d.id === distributor.id)) return prev;
      return [...prev, distributor];
    });
  };


  const onChangeCarrier = (option: XGSSelectOption | null | undefined) => {
    setCarrierFormValue(option || null);
    formikRef.current?.setFieldValue("carrierNumber", option?.value || "");
  };

  const onChangeTrailer = (option: XGSSelectOption | null | undefined) => {
    setTrailerFormValue(option || null);
    if (formikRef.current) {
      formikRef.current.setFieldValue("trailer", option?.value || "");
    }
  };

  // eslint-disable-next-line
  const searchCarrier = React.useCallback(
    debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
      carrierService.getCarrierListQuery(query, 0)
        .then(response => {
          const options = response.data.content.map(c => ({
            label: `${c.carrierName} (${c.carrierNumber})`,
            value: c.carrierNumber.toString()
          }));
          callback(options);
        })
        .catch(() => {
          callback([]);
        });
    }, 300),
    []
  );

  // eslint-disable-next-line
  const searchTrailer = React.useCallback(
    debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
      equipmentService.getEquipmentListQuery(query, 0)
        .then(response => {
          const options = response.data?.content.map(e => ({
            label: e.unitNumber || "",
            value: e.unitNumber || ""
          })) || [];
          callback(options);
        })
        .catch(() => {
          callback([]);
        });
    }, 300),
    []
  );

  const doorOptions = useMemo(() => {
    return doors
      ?.filter(d => d.doorNumber)
      .map(d => ({
        label: d.doorNumber!,
        value: d.doorNumber!
      })) || [];
  }, [doors]);


  const secondaryDistributorColumns = useMemo(() => [
    columnHelper.accessor("accountNumber", {
      id: "accountNumber",
      header: "Account#",
      size: 80,
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 115,
    }),
    columnHelper.accessor("attention", {
      id: "attention",
      header: "Attention",
      size: 75,
    }),
    columnHelper.accessor("address", {
      id: "address",
      header: "Address",
      size: 115,
    }),
    columnHelper.display({
      id: "action",
      header: "Action",
      size: 63,
      cell: (cellProps) => (
        <span
          onClick={() => handleRemoveDistributor(cellProps.row.original.id)}
          style={{ color: 'red', cursor: 'pointer', fontSize: '12px' }}
        >
          <DeleteIcon />
        </span>
      ),
    }),
  ], []);

  const resetAllFields = () => {
    const freshInitialValues = getInitialValues(props.initialValues);

    // reset formik completely
    formikRef.current?.resetForm({
      values: freshInitialValues,
    });

    // reset ALL local states
    setCarrierFormValue(null);
    setTrailerFormValue(null);
    setDriverFormValue(null);

    setCarrierSearch("");
    setTrailerSearch("");

    setSecondaryDistributors([]);
  };

  const onChangeDriver = (option: XGSSelectOption | null | undefined) => {
    setDriverFormValue(option || null);

    const cleanName = option?.label?.replace(/\s*\(\d+\)\s*$/, "") || "";

    formikRef.current?.setFieldValue("driverId", option?.value || "");
    formikRef.current?.setFieldValue("driverName", cleanName);
  };

  return (
    <>
      <SlideOutSidebar
        header="Assign Door"
        show={props.show}
        onClose={handleClose}
        size="small-500"
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          innerRef={formikRef}
          enableReinitialize={true}
          validateOnMount
          key={`assign-door-${props.manifestNumber}-${props.show ? JSON.stringify(props.initialValues || {}) : 'default'}`}
        >
          {(formikProps: FormikProps<AssignDoorFormValues>) => {
            return (
              <Form className="xgs-assign-door-modal__content">
                <div className="xgs-assign-door-modal__header-info">
                  <div className="xgs-assign-door-modal__manifest">
                    Manifest {props.manifestNumber}
                  </div>
                  {/* <div className="xgs-assign-door-modal__route">
                  {props.originTerminal || "ORD - Chicago"} <span className="xgs-assign-door-modal__route-count">77</span> → {props.destinationTerminal || "PHX - Phoenix"} <span className="xgs-assign-door-modal__route-count">75</span>
                </div> */}
                </div>


                <div className="xgs-assign-door-modal__fields">
                  <div className="xgs-assign-door-modal__row">
                    <XGSFormSelect
                      name="doorNumber"
                      label="Door"
                      labelMode={LabelModes.column}
                      placeholder="Select Door"
                      required
                      options={doorOptions}
                      controlled
                      isLoading={isDoorsLoading}
                      className="xgs-assign-door-modal__field"
                    />

                    <XGSFormSelect
                      name="priority"
                      label="Door Priority"
                      labelMode={LabelModes.column}
                      placeholder="Select Priority"
                      required
                      options={PRIORITY_OPTIONS}
                      controlled
                      className="xgs-assign-door-modal__field"
                    />
                  </div>

                  <div className="xgs-assign-door-modal__row">
                    <XGSFormSelect
                      name="doorUsage"
                      required
                      label="Door Usage"
                      labelMode={LabelModes.column}
                      placeholder="Select Door Usage"
                      options={DOOR_USAGE_OPTIONS}
                      controlled
                      className="xgs-assign-door-modal__field"
                    />

                    <XGSFormSelect
                      name="doorType"
                      label="Door Type"
                      required
                      labelMode={LabelModes.column}
                      placeholder="Select Door Type"
                      options={DOOR_TYPE_OPTIONS}
                      controlled
                      className="xgs-assign-door-modal__field"
                    />
                  </div>

                  <LabeledSelectInput
                    name="driverId"
                    label="Driver"
                    labelMode={LabelModes.column}
                    placeholder="Start typing..."
                    value={driverFormValue}
                    onValueChange={onChangeDriver}
                    loadOptions={loadDriverOptions}
                    cacheOptions
                    async
                    options={[]}
                    className="xgs-assign-door-modal__field"
                  />

                  <LabeledSelectInput
                    name="trailer"
                    label="Trailer"
                    labelMode={LabelModes.column}
                    placeholder="Start typing..."
                    required
                    value={trailerFormValue}
                    onValueChange={onChangeTrailer}
                    onInputChange={setTrailerSearch}
                    options={[]}
                    async
                    cacheOptions={false}
                    loadOptions={searchTrailer}
                    formik={true}
                    isClearable
                    className="xgs-assign-door-modal__field"
                  />

                  <div className="xgs-assign-door-modal__helper-text">
                    A trailer must be assigned when selecting a door.
                  </div>

                  {/* <LabeledSelectInput
                  label="Primary Distributor"
                  labelMode={LabelModes.column}
                  placeholder="Select Primary Distributor"
                  required
                  options={primaryDistributorOptions}
                  value={formikProps.values.primaryDistributor}
                  onValueChange={(option) => formikProps.setFieldValue("primaryDistributor", option)}
                  className="xgs-assign-door-modal__field"
                  isDisabled={true}
                /> */}


                  <div className="xgs-assign-door-modal__section">
                    <div className="xgs-assign-door-modal__section-title">
                      Secondary Distributors
                    </div>

                    {secondaryDistributors.length > 0 && (
                      <div className="xgs-assign-door-modal__table">
                        <Table
                          columns={secondaryDistributorColumns}
                          data={secondaryDistributors}
                          // infiniteScroll
                          responsive
                          noResultsText="No secondary distributors"
                        />
                      </div>
                    )}

                    <div
                      className="xgs-assign-door-modal__add-button"
                      onClick={secondaryDistributors.length < 4 ? handleAddSecondaryDistributor : undefined}
                      style={{
                        opacity: secondaryDistributors.length >= 4 ? 0.5 : 1,
                        cursor: secondaryDistributors.length >= 4 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      + Add Secondary Distributors {secondaryDistributors.length >= 4 && '(Max 4)'}
                    </div>
                  </div>

                  {/* <XGSFormSelect
                  name="commodityCode"
                  label="Commodity"
                  labelMode={LabelModes.column}
                  options={commonState.commodityCodes}
                  className="xgs-pickup__field"
                  isClearable
                  isSearchable
                  async
                  controlled
                  placeholder="Select..."
                /> */}
                  <LabeledSelectInput
                    name="carrierNumber"
                    label="Carrier"
                    labelMode={LabelModes.column}
                    placeholder="Start typing..."
                    value={carrierFormValue}
                    onValueChange={onChangeCarrier}
                    onInputChange={setCarrierSearch}
                    options={[]}
                    async
                    cacheOptions={false}
                    loadOptions={searchCarrier}
                    formik={true}
                    isClearable
                    className="xgs-assign-door-modal__field"
                  />
                </div>

                <div className="xgs-assign-door-modal__buttons">
                  <Button
                    theme={ButtonThemes.gray}
                    type="button"
                    onClick={handleClose}
                    className="xgs-assign-door-modal__button"
                    disabled={assignDoorMutation.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    theme={ButtonThemes.blue}
                    type="submit"
                    disabled={
                      !formikProps.isValid ||
                      !formikProps.dirty ||
                      assignDoorMutation.isLoading ||
                      originTerminalNumber == null
                    }
                    spinner={assignDoorMutation.isLoading}
                    className="xgs-assign-door-modal__button"
                  >
                    Save
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </SlideOutSidebar>

      <AddSecondaryDistributorModal
        show={showAddDistributorModal}
        onClose={() => setShowAddDistributorModal(false)}
        onAdd={handleAddDistributor}
        existingDistributorIds={secondaryDistributors.map(d => d.id)}
      />
    </>
  );
};

export default AssignDoorModal;